import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Paper
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: "600px",
    minHeight: "400px",
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "flex-start",
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  activeFiltersContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
}));

// Campos padrão da tabela cliente
const STANDARD_FIELDS = [
  { value: "NOMECLI", label: "Nome do Cliente", type: "text" },
  { value: "NOMEFANTASIA", label: "Nome Fantasia", type: "text" },
  { value: "CGCCLI", label: "CNPJ", type: "text" },
  { value: "CPFCLI", label: "CPF", type: "text" },
  { value: "E_MAIL", label: "E-mail", type: "text" },
  { value: "FONECLI", label: "Telefone", type: "text" },
  { value: "CELULAR", label: "Celular", type: "text" },
  { value: "CIDADECLI", label: "Cidade", type: "text" },
  { value: "ESTADOCLI", label: "Estado", type: "text" },
  { value: "BAIRROCLI", label: "Bairro", type: "text" },
  { value: "CEPCLI", label: "CEP", type: "text" },
  { value: "STATUS", label: "Status", type: "select", options: ["A", "I"] },
  { value: "CRM_SITUACAO_CLIENTE", label: "Situação CRM", type: "text" },
  { value: "TIPO_CLIENTE", label: "Tipo de Cliente", type: "text" },
  { value: "PESSOA", label: "Tipo Pessoa", type: "select", options: ["F", "J"] },
  { value: "DATA_ULTIMA_COMPRA", label: "Data Última Compra", type: "date" },
  { value: "DATA_PRIMEIRA_COMPRA", label: "Data Primeira Compra", type: "date" },
  { value: "DTCADASTRO", label: "Data Cadastro", type: "date" },
];

// Operadores disponíveis por tipo de campo
const OPERATORS = {
  text: [
    { value: "contains", label: "Contém" },
    { value: "equals", label: "Igual a" },
    { value: "not_equals", label: "Diferente de" },
    { value: "starts_with", label: "Começa com" },
    { value: "ends_with", label: "Termina com" },
    { value: "is_empty", label: "Está vazio" },
    { value: "is_not_empty", label: "Não está vazio" },
  ],
  number: [
    { value: "equals", label: "Igual a" },
    { value: "not_equals", label: "Diferente de" },
    { value: "greater_than", label: "Maior que" },
    { value: "less_than", label: "Menor que" },
    { value: "greater_or_equal", label: "Maior ou igual a" },
    { value: "less_or_equal", label: "Menor ou igual a" },
    { value: "is_empty", label: "Está vazio" },
    { value: "is_not_empty", label: "Não está vazio" },
  ],
  date: [
    { value: "equals", label: "Igual a" },
    { value: "before", label: "Antes de" },
    { value: "after", label: "Depois de" },
    { value: "between", label: "Entre" },
    { value: "is_empty", label: "Está vazio" },
    { value: "is_not_empty", label: "Não está vazio" },
  ],
  select: [
    { value: "equals", label: "Igual a" },
    { value: "not_equals", label: "Diferente de" },
    { value: "is_empty", label: "Está vazio" },
    { value: "is_not_empty", label: "Não está vazio" },
  ],
  multiselect: [
    { value: "contains", label: "Contém" },
    { value: "not_contains", label: "Não contém" },
    { value: "is_empty", label: "Está vazio" },
    { value: "is_not_empty", label: "Não está vazio" },
  ],
  checkbox: [
    { value: "equals", label: "Igual a" },
  ],
};

const ClienteFilterModal = ({ open, onClose, onApplyFilters, currentFilters = [] }) => {
  const classes = useStyles();
  const [filters, setFilters] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadCustomFieldDefinitions();
      if (currentFilters.length > 0) {
        setFilters(currentFilters);
      } else {
        setFilters([createEmptyFilter()]);
      }
    }
  }, [open, currentFilters]);

  const loadCustomFieldDefinitions = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/cliente-custom-field-definitions");
      console.log("Campos personalizados carregados:", data);
      
      // Garantir que sempre seja um array
      const definitions = Array.isArray(data?.definitions) 
        ? data.definitions 
        : Array.isArray(data) 
        ? data 
        : [];
      
      setCustomFieldDefinitions(definitions);
    } catch (error) {
      console.error("Erro ao carregar definições de campos personalizados:", error);
      // Não mostrar erro para o usuário se for só ausência de campos
      setCustomFieldDefinitions([]);
    } finally {
      setLoading(false);
    }
  };

  const createEmptyFilter = () => ({
    id: Date.now(),
    fieldType: "standard", // 'standard' ou 'custom'
    field: "",
    operator: "",
    value: "",
    value2: "", // Para operadores "between"
  });

  const addFilter = () => {
    setFilters([...filters, createEmptyFilter()]);
  };

  const removeFilter = (filterId) => {
    setFilters(filters.filter((f) => f.id !== filterId));
  };

  const updateFilter = (filterId, updates) => {
    setFilters(
      filters.map((f) =>
        f.id === filterId ? { ...f, ...updates } : f
      )
    );
  };

  const getFieldConfig = (filter) => {
    if (filter.fieldType === "standard") {
      return STANDARD_FIELDS.find((f) => f.value === filter.field);
    } else if (filter.fieldType === "custom") {
      const customField = customFieldDefinitions.find((f) => f.fieldName === filter.field);
      if (customField) {
        return {
          value: customField.fieldName,
          label: customField.fieldLabel,
          type: customField.fieldType,
          options: customField.fieldOptions,
        };
      }
    }
    return null;
  };

  const getOperatorsForField = (filter) => {
    const fieldConfig = getFieldConfig(filter);
    if (!fieldConfig) return [];
    return OPERATORS[fieldConfig.type] || OPERATORS.text;
  };

  const needsValue = (operator) => {
    return !["is_empty", "is_not_empty"].includes(operator);
  };

  const needsSecondValue = (operator) => {
    return operator === "between";
  };

  const handleApply = () => {
    const validFilters = filters.filter(
      (f) => f.field && f.operator && (needsValue(f.operator) ? f.value : true)
    );
    onApplyFilters(validFilters);
    onClose();
  };

  const handleClear = () => {
    setFilters([createEmptyFilter()]);
    onApplyFilters([]);
  };

  const renderValueInput = (filter) => {
    const fieldConfig = getFieldConfig(filter);
    if (!fieldConfig || !needsValue(filter.operator)) return null;

    const commonProps = {
      fullWidth: true,
      size: "small",
      variant: "outlined",
      value: filter.value || "",
      onChange: (e) => updateFilter(filter.id, { value: e.target.value }),
    };

    switch (fieldConfig.type) {
      case "select":
        const selectOptions = fieldConfig.options || [];
        return (
          <TextField
            {...commonProps}
            select
            label="Valor"
          >
            {selectOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );

      case "multiselect":
        const multiselectOptions = fieldConfig.options || [];
        return (
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Valor</InputLabel>
            <Select
              multiple
              value={filter.value ? filter.value.split(",") : []}
              onChange={(e) =>
                updateFilter(filter.id, { value: e.target.value.join(",") })
              }
              renderValue={(selected) => (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {multiselectOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={filter.value?.split(",").includes(option)} />
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={filter.value === "true"}
                onChange={(e) =>
                  updateFilter(filter.id, { value: e.target.checked ? "true" : "false" })
                }
              />
            }
            label="Marcado"
          />
        );

      case "date":
        return (
          <>
            <TextField
              {...commonProps}
              type="date"
              label="Data"
              InputLabelProps={{ shrink: true }}
            />
            {needsSecondValue(filter.operator) && (
              <TextField
                {...commonProps}
                type="date"
                label="Data Final"
                value={filter.value2 || ""}
                onChange={(e) => updateFilter(filter.id, { value2: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </>
        );

      case "number":
        return (
          <TextField
            {...commonProps}
            type="number"
            label="Valor"
          />
        );

      default:
        return (
          <TextField
            {...commonProps}
            label="Valor"
            placeholder="Digite o valor..."
          />
        );
    }
  };

  const getFilterDescription = (filter) => {
    const fieldConfig = getFieldConfig(filter);
    if (!fieldConfig) return null;

    const operator = getOperatorsForField(filter).find((op) => op.value === filter.operator);
    const operatorLabel = operator ? operator.label.toLowerCase() : filter.operator;

    let valueDisplay = filter.value;
    if (filter.operator === "between" && filter.value2) {
      valueDisplay = `${filter.value} e ${filter.value2}`;
    }

    return (
      <span>
        <strong>{fieldConfig.label}</strong> {operatorLabel}{" "}
        {needsValue(filter.operator) && <strong>{valueDisplay}</strong>}
      </span>
    );
  };

  const activeFilters = filters.filter((f) => f.field && f.operator);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FilterIcon />
          <Typography variant="h6">Filtros Personalizados de Clientes</Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {activeFilters.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Filtros Ativos:
            </Typography>
            <Box className={classes.activeFiltersContainer}>
              {activeFilters.map((filter) => (
                <Chip
                  key={filter.id}
                  label={getFilterDescription(filter)}
                  onDelete={() => removeFilter(filter.id)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Adicionar Filtros:
        </Typography>

        {filters.map((filter, index) => (
          <Paper key={filter.id} elevation={1} style={{ padding: 16, marginBottom: 16 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Tipo de Campo"
                  value={filter.fieldType}
                  onChange={(e) => {
                    updateFilter(filter.id, {
                      fieldType: e.target.value,
                      field: "",
                      operator: "",
                      value: "",
                    });
                  }}
                >
                  <MenuItem value="standard">Campo Padrão</MenuItem>
                  <MenuItem value="custom">Campo Personalizado</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Campo"
                  value={filter.field}
                  onChange={(e) => {
                    updateFilter(filter.id, {
                      field: e.target.value,
                      operator: "",
                      value: "",
                    });
                  }}
                  disabled={!filter.fieldType}
                >
                  {filter.fieldType === "standard" &&
                    STANDARD_FIELDS.map((field) => (
                      <MenuItem key={field.value} value={field.value}>
                        {field.label}
                      </MenuItem>
                    ))}
                  {filter.fieldType === "custom" &&
                    Array.isArray(customFieldDefinitions) &&
                    customFieldDefinitions.length > 0 ? (
                      customFieldDefinitions.map((field) => (
                        <MenuItem key={field.fieldName} value={field.fieldName}>
                          {field.fieldLabel}
                        </MenuItem>
                      ))
                    ) : filter.fieldType === "custom" ? (
                      <MenuItem disabled>
                        Nenhum campo personalizado cadastrado
                      </MenuItem>
                    ) : null}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Operador"
                  value={filter.operator}
                  onChange={(e) => {
                    updateFilter(filter.id, { operator: e.target.value, value: "" });
                  }}
                  disabled={!filter.field}
                >
                  {getOperatorsForField(filter).map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={filter.operator ? 2 : 3}>
                {renderValueInput(filter)}
              </Grid>

              <Grid item xs={12} sm={1} style={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => removeFilter(filter.id)}
                  disabled={filters.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))}

        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addFilter}
          className={classes.addButton}
        >
          Adicionar Filtro
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear} startIcon={<ClearIcon />}>
          Limpar Filtros
        </Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          Aplicar Filtros
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClienteFilterModal;
